import { computed } from '@angular/core';
import { DEFAULT_PERSONALIZATION_MAX_LENGTH } from '@app/service/student-detail/student-personalization.constants';
import { isPersonalizationNameValid } from '@app/service/student-detail/student-personalization.validation';
import UpgradeType from '@app/store/UpgradeType.js';
import type {
  EnhancementEntity,
  EnhancementSelectedUpgrades,
  EnhancementSelectorStore,
  EnhancementTemplate,
  EnhancementTemplateMap,
  EnhancementUpgrade
} from '@app/store/data/enhancement/enhancement.types';
import { sanitizeInput } from '@app/utils/sanitize';

// eslint-disable-next-line max-lines-per-function
export const ENHANCEMENT_SELECTORS = (store: EnhancementSelectorStore) => ({
  getUpgrades: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => entityMap[subjectId];
  }),
  getRetouchUpgrade: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      if (!entity || entity.upgrades.length === 0) {
        return null;
      }
      const selectedUpgrades = entity.selectedUpgrades;
      const retouch = selectedUpgrades.retouch;

      if (!retouch) {
        return null;
      }
      return entity.upgrades.find((el: EnhancementUpgrade) => el.type === retouch) ?? null;
    };
  }),
  getSelectedRetouch: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      return entity?.selectedUpgrades.retouch ?? null;
    };
  }),
  getSelectedPersonalization: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      return entity?.selectedUpgrades[UpgradeType.PERSONALIZATION]?.skuCode ?? null;
    };
  }),
  hasPersonalization: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      if (!entity) {
        return false;
      }

      return (
        entity.upgrades.some((upgrade: EnhancementUpgrade) => upgrade.type === UpgradeType.PERSONALIZATION) ||
        Boolean(entity.selectedUpgrades[UpgradeType.PERSONALIZATION]?.skuCode)
      );
    };
  }),
  getPersonalizationMaxLength: computed(() => {
    const entityMap = store.entityMap();
    const templateMap = store.templateMap();

    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      return getPersonalizationMaxLength(entity, templateMap);
    };
  }),
  getPersonalizationUpgrade: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      if (!entity || entity.upgrades.length === 0) {
        return null;
      }
      const selectedUpgrades = entity.selectedUpgrades;
      const personalizationSkuCode = selectedUpgrades[UpgradeType.PERSONALIZATION]?.skuCode;
      if (!personalizationSkuCode) {
        return null;
      }

      const personalization = entity.upgrades.find(
        (el: EnhancementUpgrade) => el.type === UpgradeType.PERSONALIZATION && el.skuCode === personalizationSkuCode
      );
      if (!personalization) {
        return null;
      }
      const templateMap = personalization.templateName ? store.templateMap()[personalization.templateName] : undefined;

      return updatePersonalizationName(personalization, templateMap, selectedUpgrades);
    };
  }),
  getPersonalizationName: computed(() => {
    const entityMap = store.entityMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      const selectedUpgrades = entity?.selectedUpgrades;

      return sanitizeInput(selectedUpgrades?.[UpgradeType.PERSONALIZATION]?.value) ?? '';
    };
  }),
  getPersonalizationValidationStatus: computed(() => {
    const entityMap = store.entityMap();
    const templateMap = store.templateMap();
    return (subjectId: string) => {
      const entity = entityMap[subjectId];
      if (!entity || entity.upgrades.length === 0) {
        return true;
      }
      const selectedUpgrades = entity.selectedUpgrades;
      const personalizationEnable = selectedUpgrades[UpgradeType.PERSONALIZATION]?.skuCode;
      const upgradeValue = sanitizeInput(selectedUpgrades[UpgradeType.PERSONALIZATION]?.value) ?? '';

      if (!personalizationEnable) {
        return true;
      }

      const maxLength = getPersonalizationMaxLength(entity, templateMap);
      return isPersonalizationNameValid(upgradeValue, maxLength);
    };
  })
});

function getPersonalizationMaxLength(
  entity: EnhancementEntity | undefined,
  templateMap: EnhancementTemplateMap
): number {
  const templateName = entity?.upgrades.find(
    (upgrade: EnhancementUpgrade) => upgrade.type === UpgradeType.PERSONALIZATION
  )?.templateName;
  const templateField = templateName ? templateMap[templateName]?.fields?.field?.[0] : null;

  return templateField?.length ?? DEFAULT_PERSONALIZATION_MAX_LENGTH;
}

function updatePersonalizationName(
  personalization: EnhancementUpgrade,
  templateMap: EnhancementTemplate | undefined,
  selectedUpgrades: EnhancementSelectedUpgrades
): EnhancementUpgrade {
  return {
    ...personalization,
    selectionTemplateMap: templateMap,
    orderLineItemSelections: [
      {
        selectionFieldName: templateMap?.fields?.field?.[0]?.name,
        label: templateMap?.fields?.field?.[0]?.label,
        type: templateMap?.fields?.field?.[0]?.labType,
        value: sanitizeInput(selectedUpgrades[UpgradeType.PERSONALIZATION]?.value) ?? ''
      }
    ]
  };
}
